import React, { useEffect, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import AssignIcon from 'assets/LeadInfo/assign.svg';
import ReassignIcon from 'assets/LeadInfo/reassign.svg';
import TakeLeadIcon from 'assets/LeadInfo/takeLead.svg';
// import ExportIcon from 'assets/exportBlack.svg';
import FilterIcon from 'assets/filter.svg';
import closeIcon from 'assets/filters/close.svg';
import DateIcon from 'assets/myLeads/date.svg';
import VectorIcon from 'assets/myLeads/vector.svg';
import ResetIcon from 'assets/reset.svg';
import SearchIcon from 'assets/search.svg';
import Button from 'common-ui/button';
import CustomDatePicker from 'common-ui/customDatePicker';
import Input from 'common-ui/input';
import { Select } from 'common-ui/select';
import AsyncSelect from 'common-ui/select/AsyncSelect';
import CustomSelectComponent from 'common-ui/select/Select';
import { selectUserInfo, selectUserType } from 'features/auth/authSlice';
import { getBranches } from 'features/branches/branchesActions';
import { getDepartments } from 'features/departments/departmentsActions';
import {
  bulkChangeLeadStatus,
  getAdministrativeDistricts,
  getAdministrativeRegions,
  getCities,
  getLeadSources,
  getOffers,
  getProducts,
  getWorkflowStatuses,
} from 'features/newsales/newsalesActions';
import { getTeams } from 'features/teams/teamsActions';
import { getSubordinates, getUsersDropdown } from 'features/users/usersActions';
import useDebounce from 'hooks/useDebounce';

import {
  APPOINTMENT_OPTIONS,
  INITIAL_FIELD_DATA,
  PRIORITY_OPTION,
} from '../../MyLeadConstants';
import {
  ActionIcon,
  BadgeItemStatus,
  CardRightIcon,
  ClearAllRow,
  ClearAllText,
  CloseIconWrapper,
  FilterContainer,
  FilterLabel,
  FilterRow,
  FilterRowLine, // HiddenFileInput,
  Icon,
  IconButton,
  IconButtonBox,
  InputWrapper,
  Line,
  MainActionsRow,
  MenuHeader,
  MenuScrollContainer,
  OptionLabel,
  OptionWrapper,
  ResetButton,
  SelectBox,
  SortButton,
  StatusDot,
} from './SearchAndFilter.styles';

const SearchAndFilter = ({
  columnDefinitions = [],
  selectedLeadIds = [],
  columns = {},
  setColumns,
  setSearchData,
  resetSearchData,
  onSelectionChange,
  onAssign,
  onReassign,
  onTakeLead,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userType = useSelector(selectUserType);
  const userInfo = useSelector(selectUserInfo);

  const ASSIGN_ROLES = [
    'Super Admin',
    'General Manager',
    'Department Head',
    'Branch Head',
    'Team Lead',
  ];
  const canAssign = ASSIGN_ROLES.includes(userType);

  const shouldShowDepartmentFilter = !['Branch Head', 'Team Lead', 'Team Member'].includes(
    userType
  );
  const shouldShowBranchFilter = !['Team Lead', 'Team Member'].includes(userType);
  const shouldShowTeamFilter = userType !== 'Team Member';
  const shouldShowAssigneeFilter = userType !== 'Team Member';

  const [showFilters, setShowFilters] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [sortOrder, setSortOrder] = useState('DESC');

  const [fieldData, setFieldData] = useState(INITIAL_FIELD_DATA);
  const [focusedField, setFocusedField] = useState(null);

  // Dynamic filter options state
  const [workflowStatuses, setWorkflowStatuses] = useState([]);
  const [products, setProducts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  // const fileInputRef = useRef(null);

  const debouncedFullName = useDebounce(fieldData.searchName, 500);
  const debouncedCompanyName = useDebounce(fieldData.searchCompanyName, 500);
  const debouncedCommunicationMethod = useDebounce(fieldData.searchMethod, 500);
  const debouncedLeadId = useDebounce(fieldData.searchId, 500);

  const selectedLeads = [];
  Object.values(columns).forEach((columnLeads) => {
    columnLeads.forEach((lead) => {
      if (selectedLeadIds.includes(lead.id)) {
        selectedLeads.push(lead);
      }
    });
  });

  // Fetch dynamic filter options on component mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        // Fetch workflow statuses
        const workflowStatusesResponse = await dispatch(
          getWorkflowStatuses({ page: 1, limit: 100 })
        ).unwrap();
        const statusesData = Array.isArray(workflowStatusesResponse)
          ? workflowStatusesResponse
          : workflowStatusesResponse?.data || [];
        setWorkflowStatuses(statusesData);

        // Fetch products
        const productsResponse = await dispatch(getProducts({ page: 1, limit: 100 })).unwrap();
        const productsData = Array.isArray(productsResponse)
          ? productsResponse
          : productsResponse?.data || [];
        setProducts(productsData);

        // Fetch offers with pagination
        let allOffers = [];
        let page = 1;
        const limit = 12;
        let hasMore = true;

        while (hasMore) {
          const offersResponse = await dispatch(getOffers({ page, limit })).unwrap();
          const responseData = Array.isArray(offersResponse)
            ? offersResponse
            : offersResponse?.data || [];
          allOffers = [...allOffers, ...responseData];

          hasMore = responseData.length === limit;
          page += 1;
        }
        setOffers(allOffers);

        // Fetch lead sources
        const leadSourcesResponse = await dispatch(
          getLeadSources({ page: 1, limit: 100 })
        ).unwrap();
        const leadSourcesData = Array.isArray(leadSourcesResponse)
          ? leadSourcesResponse
          : leadSourcesResponse?.data || [];
        setLeadSources(leadSourcesData);
      } catch (error) {
        console.error('Failed to fetch filter options:', error);
      }
    };

    fetchFilterOptions();
  }, [dispatch]);

  // Load options for AsyncSelect - Departments
  const loadDepartmentOptions = async () => {
    try {
      const params = { page: 1, limit: 100 };

      if (userType === 'Department Head' && userInfo?.departmentId) {
        params.departmentId = userInfo.departmentId;
      } else if (userType === 'Branch Head' && userInfo?.departmentId) {
        params.departmentId = userInfo.departmentId;
      }

      const departmentsResponse = await dispatch(getDepartments(params)).unwrap();
      const departmentsData =
        departmentsResponse?.departments || departmentsResponse?.data || [];
      return departmentsData.map((dept) => ({
        value: dept.uuid,
        label: dept.name,
      }));
    } catch (error) {
      console.error('Failed to fetch departments:', error);
      return [];
    }
  };

  // Load options for AsyncSelect - Branches
  const loadBranchOptions = async () => {
    try {
      const params = { page: 1, limit: 100, offset: 0 };

      if (fieldData.department?.value) {
        params.departmentId = fieldData.department.value;
      } else if (userType === 'Department Head' && userInfo?.departmentId) {
        params.departmentId = userInfo.departmentId;
      } else if (userType === 'Branch Head' && userInfo?.branchId) {
        params.branchId = userInfo.branchId;
      }

      const branchesResponse = await dispatch(getBranches(params)).unwrap();
      const branchesData = branchesResponse?.branches || branchesResponse?.data || [];
      return branchesData.map((branch) => ({
        value: branch.uuid,
        label: branch.name,
      }));
    } catch (error) {
      console.error('Failed to fetch branches:', error);
      return [];
    }
  };

  // Load options for AsyncSelect - Teams
  const loadTeamOptions = async () => {
    try {
      const params = { page: 1, limit: 100, offset: 0 };

      if (fieldData.branch?.value) {
        params.branchId = fieldData.branch.value;
      } else if (fieldData.department?.value) {
        params.departmentId = fieldData.department.value;
      } else if (userType === 'Department Head' && userInfo?.departmentId) {
        params.departmentId = userInfo.departmentId;
      } else if (userType === 'Branch Head' && userInfo?.branchId) {
        params.branchId = userInfo.branchId;
      } else if (userType === 'Team Lead' && userInfo?.teamId) {
        params.teamId = userInfo.teamId;
      }

      const teamsResponse = await dispatch(getTeams(params)).unwrap();
      const teamsData = teamsResponse?.teams || teamsResponse?.data || [];
      return teamsData.map((team) => ({
        value: team.uuid,
        label: team.name,
      }));
    } catch (error) {
      console.error('Failed to fetch teams:', error);
      return [];
    }
  };

  const loadAssigneeOptions = async (searchTerm) => {
    try {
      let usersData = [];
      const params = {
        page: 1,
        limit: 100,
        offset: 0,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (fieldData.department?.value) {
        params.departmentId = fieldData.department.value;
      }
      if (fieldData.branch?.value) {
        params.branchId = fieldData.branch.value;
      }
      if (fieldData.team?.value) {
        params.teamId = fieldData.team.value;
      }

      if (userType === 'Team Lead') {
        const subordinatesResponse = await dispatch(getSubordinates(params)).unwrap();
        usersData = subordinatesResponse?.users || subordinatesResponse?.data || [];
      } else {
        const usersResponse = await dispatch(getUsersDropdown(params)).unwrap();
        usersData = usersResponse?.users || usersResponse?.data || [];
      }

      return usersData.map((user) => ({
        value: user.uuid || user.id,
        label:
          user.name && user.surname
            ? `${user.name} ${user.surname}`
            : user.name || user.surname || user.email,
      }));
    } catch (error) {
      console.error('Failed to fetch assignees:', error);
      return [];
    }
  };

  const loadDistrictOptions = async () => {
    try {
      const params = {
        limit: 1000,
      };

      const result = await dispatch(getAdministrativeRegions(params));
      const districtsData = result.payload?.data || [];
      return districtsData.map((district) => ({
        value: district.id,
        label: district.name,
      }));
    } catch (error) {
      console.error('Failed to fetch districts:', error);
      return [];
    }
  };

  const loadCityOptions = async () => {
    try {
      const params = {
        limit: 1000,
      };

      const result = await dispatch(getCities(params));
      const citiesData = result.payload?.data || [];
      return citiesData.map((city) => ({
        value: city.id,
        label: city.name,
      }));
    } catch (error) {
      console.error('Failed to fetch cities:', error);
      return [];
    }
  };

  const loadAdministrativeRegionOptions = async () => {
    try {
      const params = {
        limit: 1000,
      };

      const result = await dispatch(getAdministrativeDistricts(params));
      const regionsData = result.payload?.data || [];
      return regionsData.map((region) => ({
        value: region.id,
        label: region.name,
      }));
    } catch (error) {
      console.error('Failed to fetch administrative regions:', error);
      return [];
    }
  };

  useEffect(() => {
    const searchParams = {
      fullName: debouncedFullName?.trim() || undefined,
      companyName: debouncedCompanyName?.trim() || undefined,
      communicationMethod: debouncedCommunicationMethod?.trim() || undefined,
      leadNumber: debouncedLeadId?.trim() || undefined,
    };

    setSearchData((prev) => ({
      ...prev,
      ...searchParams,
    }));
  }, [
    JSON.stringify({
      debouncedFullName,
      debouncedCompanyName,
      debouncedCommunicationMethod,
      debouncedLeadId,
    }),
  ]);

  useEffect(() => {
    const filterParams = {
      workflowStatusId: fieldData.status?.value || undefined,
      leadSourceId: fieldData.source?.value || undefined,
      productId: fieldData.product?.value || undefined,
      offerId: fieldData.offer?.value || undefined,
      priority: fieldData.priority?.value ? fieldData.priority.value.toUpperCase() : undefined,
      departmentId: fieldData.department?.value || undefined,
      branchId: fieldData.branch?.value || undefined,
      teamId: fieldData.team?.value || undefined,
      withAppointment:
        fieldData.appointment?.value === 'with'
          ? true
          : fieldData.appointment?.value === 'without'
            ? false
            : undefined,
      assigneeUuid: selectedAssignee?.value || undefined,
      districtId: fieldData.district?.value || undefined,
      cityId: fieldData.city?.value || undefined,
      administrativeRegionId: fieldData.administrativeRegion?.value || undefined,
      createdDateFrom: fieldData.createdFrom || undefined,
      createdDateTo: fieldData.createdTo || undefined,
      activityDateFrom: fieldData.lastActivityFrom || undefined,
      activityDateTo: fieldData.lastActivityTo || undefined,
      assignDateFrom: fieldData.assignedFrom || undefined,
      assignDateTo: fieldData.assignedTo || undefined,
      sortOrder: sortOrder,
    };

    setSearchData((prev) => {
      const updated = { ...prev, ...filterParams };
      return updated;
    });
  }, [JSON.stringify(fieldData), selectedAssignee?.value, sortOrder]);

  const handleSearchChange = (field, value) => {
    setFieldData({ ...fieldData, [field]: value });
  };

  const handleSortToggle = () => {
    setSortOrder((prev) => (prev === 'DESC' ? 'ASC' : 'DESC'));
  };

  const handleReset = () => {
    setFieldData(INITIAL_FIELD_DATA);
    setSelectedAssignee(null);
    setSortOrder('DESC');
    resetSearchData();
  };

  const handleCloseFilters = () => {
    setShowFilters(false);
    const resetFilters = {
      ...INITIAL_FIELD_DATA,
      searchId: fieldData.searchId,
      searchName: fieldData.searchName,
      searchCompanyName: fieldData.searchCompanyName,
      searchMethod: fieldData.searchMethod,
    };
    setFieldData(resetFilters);
    setSelectedAssignee(null);
    setSortOrder('DESC');

    // Clear filter parameters from search data
    setSearchData((prev) => {
      const filterKeys = [
        'workflowStatusId',
        'leadSourceId',
        'productId',
        'offerId',
        'priority',
        'departmentId',
        'branchId',
        'teamId',
        'withAppointment',
        'assigneeUuid',
        'districtId',
        'cityId',
        'administrativeRegionId',
        'createdDateFrom',
        'createdDateTo',
        'activityDateFrom',
        'activityDateTo',
        'assignDateFrom',
        'assignDateTo',
        'sortOrder',
      ];

      const clearedFilters = filterKeys.reduce((acc, key) => {
        acc[key] = undefined;
        return acc;
      }, {});

      return {
        ...prev,
        ...clearedFilters,
        sortOrder: 'DESC',
      };
    });
  };

  const { control, reset } = useForm({
    defaultValues: {
      status: null,
    },
  });

  const statusOptions = columnDefinitions.map((col) => ({
    value: col.id,
    label: col.title,
    color: col.color,
  }));

  const formatOptionLabel = (option) => (
    <OptionWrapper>
      <StatusDot $color={option.color} />
      <OptionLabel>{option.label}</OptionLabel>
    </OptionWrapper>
  );

  const CustomMenuList = (props) => (
    <div>
      <MenuHeader>Change Status To:</MenuHeader>
      <MenuScrollContainer>{props.children}</MenuScrollContainer>
    </div>
  );

  // Convert fetched data to select options format
  const workflowStatusOptions = workflowStatuses.map((status) => ({
    value: status.id,
    label: status.name,
  }));

  const productOptions = products.map((product) => ({
    value: product.id,
    label: product.name,
  }));

  const offerOptions = offers.map((offer) => ({
    value: offer.id,
    label: offer.name,
  }));

  const sourceOptions = leadSources.map((source) => ({
    value: source.id,
    label: source.name,
  }));

  const priorityOptions = PRIORITY_OPTION.map((priority) => ({
    value: priority.id,
    label: priority.label,
  }));

  const handleBulkStatusChange = async (selectedOption) => {
    if (!selectedOption || selectedLeadIds.length === 0) {
      return;
    }

    const newWorkflowStatusId = selectedOption.value;

    try {
      await dispatch(
        bulkChangeLeadStatus({
          leadIds: selectedLeadIds,
          workflowStatusId: newWorkflowStatusId,
        })
      ).unwrap();

      const updatedColumns = { ...columns };

      selectedLeadIds.forEach((leadId) => {
        Object.keys(updatedColumns).forEach((columnId) => {
          const leadIndex = updatedColumns[columnId].findIndex((lead) => lead.id === leadId);
          if (leadIndex !== -1) {
            const [lead] = updatedColumns[columnId].splice(leadIndex, 1);
            lead.status = selectedOption.value;
            lead.workflowStatusId = newWorkflowStatusId;

            if (!updatedColumns[selectedOption.value]) {
              updatedColumns[selectedOption.value] = [];
            }
            updatedColumns[selectedOption.value].push(lead);
          }
        });
      });

      setColumns(updatedColumns);

      if (onSelectionChange) {
        onSelectionChange([]);
      }

      reset({ status: null });
    } catch (error) {
      console.error('Failed to update lead statuses:', error);
    }
  };

  return (
    <FilterContainer>
      {/* <HiddenFileInput type="file" ref={fileInputRef} onChange={handleFileChange} /> */}
      <MainActionsRow>
        <div className="actions-left">
          <IconButton
            outlined
            onClick={() => {
              setShowSearch(!showSearch);
              setShowFilters(false);
            }}
            className={showSearch ? 'is-active' : ''}
          >
            <ActionIcon src={SearchIcon} alt="Search" />
            Search
          </IconButton>

          <IconButton
            outlined
            className={showFilters ? 'is-active' : ''}
            onClick={() => {
              setShowFilters(!showFilters);
              setShowSearch(false);
            }}
          >
            <ActionIcon src={FilterIcon} alt="Filter" className="filter-icon" />
            Filter
          </IconButton>

          {/* <IconButton outlined className="export-button" onClick={handleExportClick}>
            <ActionIcon src={ExportIcon} alt="Export" />
            Export
          </IconButton> */}

          <ResetButton onClick={handleReset}>
            <ActionIcon src={ResetIcon} alt="Reset" />
            Reset
          </ResetButton>
        </div>

        <IconButtonBox>
          <Button
            width={180}
            secondary
            onClick={() => navigate('/sales-new')}
            className="create-lead-btn"
          >
            + Create Lead
          </Button>
        </IconButtonBox>

        {selectedLeads.length > 0 && (
          <div className="actions-right">
            <div className="badge-div">
              {canAssign && selectedLeads.every((lead) => !lead.assigneeUuid) && (
                <BadgeItemStatus onClick={onAssign}>
                  <CardRightIcon src={AssignIcon} />
                  Assign
                </BadgeItemStatus>
              )}
              {canAssign && selectedLeads.every((lead) => !!lead.assigneeUuid) && (
                <BadgeItemStatus className="reassign" onClick={onReassign}>
                  <CardRightIcon src={ReassignIcon} />
                  Reassign
                </BadgeItemStatus>
              )}
              {selectedLeads.every((lead) => !lead.assigneeUuid) && (
                <BadgeItemStatus className="take-lead" onClick={onTakeLead}>
                  <CardRightIcon src={TakeLeadIcon} />
                  Take Lead
                </BadgeItemStatus>
              )}
            </div>
            <InputWrapper className="change-status">
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <CustomSelectComponent
                    {...field}
                    req={true}
                    options={statusOptions}
                    isSearchable={false}
                    controlShouldRenderValue={false}
                    formatOptionLabel={formatOptionLabel}
                    components={{ MenuList: CustomMenuList }}
                    value={null}
                    onChange={(option) => {
                      field.onChange(option);
                      handleBulkStatusChange(option);
                    }}
                    placeholder={
                      selectedLeadIds.length > 0
                        ? `Change Status (${selectedLeadIds.length} selected)`
                        : 'Change Status'
                    }
                    blurInputOnSelect={true}
                    isDisabled={selectedLeadIds.length === 0}
                  />
                )}
              />
            </InputWrapper>
          </div>
        )}
      </MainActionsRow>

      {showSearch && (
        <Line>
          <ClearAllRow className="close-x">
            <CloseIconWrapper onClick={() => setShowSearch(false)}>
              <Icon className="close-x-icon" src={closeIcon} />
            </CloseIconWrapper>
          </ClearAllRow>
          <FilterRow>
            <InputWrapper>
              <Input
                className="search-inputs"
                label="Full Name"
                icon={focusedField === 'fullName' || fieldData.searchName ? null : VectorIcon}
                value={fieldData.searchName}
                onFocus={() => setFocusedField('fullName')}
                onBlur={() => setFocusedField(null)}
                onChange={(e) => handleSearchChange('searchName', e.target.value)}
              />
              <Input
                className="search-inputs"
                label="Company Name"
                icon={
                  focusedField === 'company' || fieldData.searchCompanyName ? null : VectorIcon
                }
                value={fieldData.searchCompanyName}
                onFocus={() => setFocusedField('company')}
                onBlur={() => setFocusedField(null)}
                onChange={(e) => handleSearchChange('searchCompanyName', e.target.value)}
              />
              <Input
                className="search-inputs"
                label="Communication Method"
                icon={focusedField === 'method' || fieldData.searchMethod ? null : VectorIcon}
                value={fieldData.searchMethod}
                onFocus={() => setFocusedField('method')}
                onBlur={() => setFocusedField(null)}
                onChange={(e) => handleSearchChange('searchMethod', e.target.value)}
              />
              <Input
                className="search-inputs"
                label="Lead ID"
                icon={focusedField === 'leadId' || fieldData.searchId ? null : VectorIcon}
                value={fieldData.searchId}
                onFocus={() => setFocusedField('leadId')}
                onBlur={() => setFocusedField(null)}
                onChange={(e) => handleSearchChange('searchId', e.target.value)}
              />
            </InputWrapper>
          </FilterRow>
        </Line>
      )}

      {showFilters && (
        <Line>
          <ClearAllRow className="close-x">
            <CloseIconWrapper onClick={handleCloseFilters}>
              <Icon className="close-x-icon" src={closeIcon} />
            </CloseIconWrapper>
          </ClearAllRow>
          <FilterRow>
            <FilterRowLine>
              {shouldShowDepartmentFilter && (
                <div>
                  <AsyncSelect
                    loadOptions={loadDepartmentOptions}
                    placeholder="Department"
                    value={fieldData.department}
                    onChange={(val) =>
                      setFieldData({ ...fieldData, department: val, branch: null, team: null })
                    }
                    isClearable
                    defaultOptions
                  />
                </div>
              )}
              {shouldShowBranchFilter && (
                <div>
                  <AsyncSelect
                    key={`branch-${fieldData.department?.value || 'all'}`}
                    loadOptions={loadBranchOptions}
                    placeholder="Branch"
                    value={fieldData.branch}
                    onChange={(val) => setFieldData({ ...fieldData, branch: val, team: null })}
                    isClearable
                    defaultOptions
                  />
                </div>
              )}
              {shouldShowTeamFilter && (
                <div>
                  <AsyncSelect
                    key={`team-${fieldData.branch?.value || fieldData.department?.value || 'all'}`}
                    loadOptions={loadTeamOptions}
                    placeholder="Team"
                    value={fieldData.team}
                    onChange={(val) => setFieldData({ ...fieldData, team: val })}
                    isClearable
                    defaultOptions
                  />
                </div>
              )}
              {shouldShowAssigneeFilter && (
                <div>
                  <AsyncSelect
                    loadOptions={loadAssigneeOptions}
                    placeholder="Assignee"
                    value={selectedAssignee}
                    onChange={(val) => setSelectedAssignee(val)}
                    isClearable
                    defaultOptions
                  />
                </div>
              )}
              <div>
                <AsyncSelect
                  loadOptions={loadDistrictOptions}
                  placeholder="Region"
                  value={fieldData.district}
                  onChange={(val) =>
                    setFieldData({
                      ...fieldData,
                      district: val,
                      city: null,
                      administrativeRegion: null,
                    })
                  }
                  isClearable
                  defaultOptions
                />
              </div>
              <div>
                <AsyncSelect
                  key={`city-${fieldData.district?.value || 'all'}`}
                  loadOptions={loadCityOptions}
                  placeholder="City"
                  value={fieldData.city}
                  onChange={(val) =>
                    setFieldData({ ...fieldData, city: val, administrativeRegion: null })
                  }
                  isClearable
                  defaultOptions
                />
              </div>
              <div>
                <AsyncSelect
                  key={`region-${fieldData.district?.value || 'all'}-${fieldData.city?.value || 'all'}`}
                  loadOptions={loadAdministrativeRegionOptions}
                  placeholder="District"
                  value={fieldData.administrativeRegion}
                  onChange={(val) => setFieldData({ ...fieldData, administrativeRegion: val })}
                  isClearable
                  defaultOptions
                />
              </div>
              <div>
                <Select
                  options={productOptions}
                  placeholder="Product"
                  value={fieldData.product}
                  onChange={(val) => setFieldData({ ...fieldData, product: val })}
                  isClearable
                />
              </div>
              <div>
                <Select
                  options={offerOptions}
                  placeholder="Offer"
                  value={fieldData.offer}
                  onChange={(val) => setFieldData({ ...fieldData, offer: val })}
                  isClearable
                />
              </div>
              <div>
                <Select
                  options={sourceOptions}
                  placeholder="Source"
                  value={fieldData.source}
                  onChange={(val) => setFieldData({ ...fieldData, source: val })}
                  isClearable
                />
              </div>
              <div>
                <FilterLabel>Sort by</FilterLabel>
                <SortButton isActive={true} onClick={handleSortToggle}>
                  <ActionIcon src={DateIcon} />
                  Created Date <span>{sortOrder === 'DESC' ? 'Newest ↓' : 'Oldest ↑'}</span>
                </SortButton>
              </div>

              <div className="date-group">
                <FilterLabel>Created Date</FilterLabel>
                <div className="date-inputs">
                  <CustomDatePicker
                    placeholder="From"
                    value={fieldData.createdFrom}
                    onChange={(date) => setFieldData({ ...fieldData, createdFrom: date })}
                  />
                  <CustomDatePicker
                    placeholder="To"
                    value={fieldData.createdTo}
                    onChange={(date) => setFieldData({ ...fieldData, createdTo: date })}
                  />
                </div>
              </div>
            </FilterRowLine>

            <FilterRowLine>
              <div className="date-group">
                <FilterLabel>Last Activity Date</FilterLabel>
                <div className="date-inputs">
                  <CustomDatePicker
                    placeholder="From"
                    value={fieldData.lastActivityFrom}
                    onChange={(date) => setFieldData({ ...fieldData, lastActivityFrom: date })}
                  />
                  <CustomDatePicker
                    placeholder="To"
                    value={fieldData.lastActivityTo}
                    onChange={(date) => setFieldData({ ...fieldData, lastActivityTo: date })}
                  />
                </div>
              </div>

              <div className="date-group">
                <FilterLabel>Assigned Date</FilterLabel>
                <div className="date-inputs">
                  <CustomDatePicker
                    placeholder="From"
                    value={fieldData.assignedFrom}
                    onChange={(date) => setFieldData({ ...fieldData, assignedFrom: date })}
                  />
                  <CustomDatePicker
                    placeholder="To"
                    value={fieldData.assignedTo}
                    onChange={(date) => setFieldData({ ...fieldData, assignedTo: date })}
                  />
                </div>
              </div>

              <div>
                <Select
                  options={APPOINTMENT_OPTIONS}
                  placeholder="Appointment"
                  value={fieldData.appointment}
                  onChange={(val) => setFieldData({ ...fieldData, appointment: val })}
                />
              </div>

              <div>
                <Select
                  options={priorityOptions}
                  placeholder="Priority"
                  value={fieldData.priority}
                  onChange={(val) => setFieldData({ ...fieldData, priority: val })}
                />
              </div>

              <div>
                <Select
                  options={workflowStatusOptions}
                  placeholder="Status"
                  value={fieldData.status}
                  onChange={(val) => setFieldData({ ...fieldData, status: val })}
                  maxMenuHeight={200}
                />
              </div>
            </FilterRowLine>
          </FilterRow>

          <ClearAllRow>
            <ClearAllText onClick={handleReset}>Clear all</ClearAllText>
          </ClearAllRow>
        </Line>
      )}
    </FilterContainer>
  );
};

export default SearchAndFilter;
